<template>
  <AsidePanel
    ref="asidePanel"
    :components="components"
    data-test-id="ConfigurationAsidePanel"
    :handleBackClick="handleBackClick"
    :handleMountedCallback="handleMounted"
    :handleSave="handleSave"
    :initialTabIndex="initialTabIndex"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="configuration"
    :navigateToTab="activeTab"
    :redirectTo="redirectTo"
    :showBackButton="true"
    :viewport="viewport"
  >
    <template
      v-if="item.template"
      v-slot:avatar
    >
      <AsideIcon
        :icon="avatarIcon"
        isPolicy
      />
    </template>
    <template v-slot:leftPaneCustomContent>
      <div
        v-if="item.template && (item.template.osMetaFamily === 'darwin' || 'ios') && mdmRequirement"
        :class="$style.macAlert"
      >
        <div
          v-if="requiresSupervision"
          :class="$style.requiresSupervision"
        >
          <HeadingExtraSmall
            :hasMargin="false"
            weight="bold"
          >
            <Icon
              icon="info"
              inline
            />
            Requires Supervision
          </HeadingExtraSmall>
          <ExternalLink
            :href="supervisedDevicesKbLink"
            :showExternalLinkIcon="false"
            text="Understanding Supervised Devices"
          />
        </div>
        <div>
          <HeadingExtraSmall
            v-if="requiresSupervision"
            data-test-id="ConfigurationAsidePanel_otherRequirements"
            :hasMargin="false"
            weight="bold"
          >
            Other Requirements:
          </HeadingExtraSmall>
        </div>
        <p>
          {{ `${mdmRequirement.text} ` }}
          <a
            :href="mdmRequirement.link"
            rel="noopener"
            target="_blank"
          >Learn more</a>
        </p>
      </div>
      <div v-if="item.template && item.template.osRestrictions">
        <PolicyOsRestrictions
          :osMetaFamily="item.template.osMetaFamily"
          :osRestrictions="item.template.osRestrictions"
        />
      </div>
    </template>
  </AsidePanel>
</template>
<script>
import {
  DarwinPoliciesNamesConsts,
  FilterComponentConsts,
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  PolicyConsts,
  StoreModelMap,
  StoreModuleMap,
  iOSPoliciesNamesConsts,
} from 'client/util/Constants';
import { HeadingExtraSmall, Icon } from '@jumpcloud/ui-components';
import { browserPolicyNameToBrowser, ospPolicyNameToOs } from 'client/util/Constants/OsPatchMgmtConstants';
import { mapActions, mapGetters } from 'vuex';
import { sum } from 'lodash';
import { trialPropsData } from 'client/util/Constants/FeatureTrialConstants';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import ConfigurationActivationModal from 'client/policies/ConfigurationActivationModal.vue';
import ConfigurationDetails from 'client/policies/ConfigurationDetails.vue';
import ConfigurationGroupItemTable from 'client/components/ItemTable/ConfigurationGroupItemTable';
import ExternalLink from 'client/components/ExternalLink.vue';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PolicyOsRestrictions from 'client/policies/PolicyOsRestrictions.vue';
import PolicySystemStatusItemTable from 'client/components/ItemTable/PolicySystemStatusItemTable.vue';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemGroupApi from 'client/api/v2/SystemGroupApi';
import SystemGroupItemTable from 'client/components/ItemTable/SystemGroupItemTable.vue';
import SystemItemTable from 'client/systems/SystemItemTable/SystemItemTable.vue';
import SystemItemTableColumns from 'client/systems/SystemItemTable/SystemItemTableColumns';
import Util from 'client/util/Util';

const { ConfigurationItemType, ConfigurationGroupItemType } = ItemType;
const configurationItem = new ConfigurationItemType();
const configurationGroupItem = new ConfigurationGroupItemType();

const { create, update } = ItemActionConsts;
const {
  browserPatchManagement,
  configuration,
  configurationGroup,
  osPatchManagement,
  policy,
  system,
  systemGroup,
} = ItemTypeConsts;
const {
  ios, mac, linux, universal, windows,
} = PolicyConsts.policyTemplateTypes;
const configurationModel = StoreModelMap[configuration];
const policyGroupCollection = StoreModuleMap[configurationGroup];
const systemCollection = StoreModuleMap[system];
const systemGroupCollection = StoreModuleMap[systemGroup];

export default {
  name: 'ConfigurationAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
    ExternalLink,
    HeadingExtraSmall,
    Icon,
    PolicyOsRestrictions,
  },

  mixins: [
    AsidePanelPropsMixin,
    FeatureTrialsMixin,
  ],

  props: {
    initialTabIndex: {
      type: Number,
      default: 0,
    },
    navigateToTab: {
      type: Object,
      default: () => {},
      validator(obj) {
        if (obj) {
          return typeof obj.tabLabel !== 'undefined';
        }
        return true;
      },
    },
    templateId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activeTab: this.navigateToTab,
      configuration,
      enrollmentTypes: ['user', 'device', 'automated'],
      ios,
      itemHasBeenSaved: false,
      linux,
      mac,
      statusCount: 0,
      supervisedDevicesKbLink: KbArticleUrls.supervisedDevicePolicies,
      validationErrorMessage: 'Please review the Details Tab for missing or invalid information',
      windows,
    };
  },

  computed: {
    ...mapGetters(configurationModel, [
      'requiresSupervision',
      'isItemBeingUpdated',
      'item',
    ]),

    avatarIcon() {
      const { osMetaFamily } = this.item.template;
      switch (osMetaFamily) {
        case 'mac':
        case 'darwin':
          return 'apple';
        case 'universal':
          return 'deviceGroup';
        case 'android':
          return 'policies';
        default:
          return osMetaFamily;
      }
    },

    components() {
      const systemItemTableProps = {
        boundItemsUrl: this.itemId ? `/api/v2/policies/${this.itemId}/systems` : undefined,
        columns: [
          SystemItemTableColumns.status,
          SystemItemTableColumns.name,
          SystemItemTableColumns.os,
        ],
        inDetailsAsidePanel: true,
        panelType: ItemTableConsts.panelType.policiesAsidePanel,
        parentOS: this.item && this.item.template && this.item.template.osMetaFamily,
        parentType: ItemTypeConsts.policy,
        showRecoveryKeyButton: this.showRecoveryKeyButton,
        useDropDownFilterComponent: FilterComponentConsts.PolicySystemDropDownFilter,
      };
      if (this.showRecoveryKeyButton) {
        systemItemTableProps.columns.push(SystemItemTableColumns.recoveryKey);
      }

      const components = [
        {
          label: 'Details',
          vueComponent: ConfigurationDetails,
        },
        {
          itemType: configurationGroupItem.type,
          label: configurationGroupItem.titlePlural,
          headerLabels: {
            [create]:
                `Select ${configurationGroupItem.label} to add this ${configurationItem.label} to`,
            [update]: ` is a member of the selected ${configurationGroupItem.label}`,
          },
          vueComponent: ConfigurationGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `/api/v2/policies/${this.itemId}/memberof`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.groupAsidePanel,
          },
        },
        {
          itemType: ItemTypeConsts.deviceGroup,
          label: 'Device Groups',
          headerLabels: {
            [create]: `This configuration will be applied to all ${this.itemOs ? this.itemOs : ''} Devices within the following groups`,
            [update]: `configuration is applied to all ${this.itemOs ? this.itemOs : ''} Devices within the following groups`,
          },
          vueComponent: SystemGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `/api/v2/policies/${this.itemId}/systemgroups`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.policiesAsidePanel,
          },
        },
        {
          itemType: ItemTypeConsts.device,
          label: 'Devices',
          headerLabels: {
            [create]: 'This configuration will be applied to the selected devices',
            [update]: 'configuration is applied to the selected devices',
          },
          vueComponent: SystemItemTable,
          vueComponentProps: systemItemTableProps,
        },
      ];

      if (this.isItemBeingUpdated && this.statusCount) {
        components.push({
          itemType: ItemTypeConsts.policyStatus,
          label: 'Status',
          vueComponent: PolicySystemStatusItemTable,
          vueComponentProps: {
            emptyCollectionMessage: 'No policy status results are available.',
            inDetailsAsidePanel: true,
            policyId: this.itemId,
          },
        });
      }

      return components;
    },

    isBrowserPatchManagementPolicy() {
      const curTemplate = this.item.template ? this.item.template.name : '';
      return !!browserPolicyNameToBrowser[curTemplate];
    },

    isOsPatchManagementPolicy() {
      const curTemplate = this.item.template ? this.item.template.name : '';
      return typeof ospPolicyNameToOs[curTemplate] !== typeof undefined;
    },

    isPatchPolicy() {
      return this.isOsPatchManagementPolicy || this.isBrowserPatchManagementPolicy;
    },

    mdmRequirement() {
      let mdmRequirement = '';
      Object.values(
        { ...DarwinPoliciesNamesConsts, ...iOSPoliciesNamesConsts },
      ).forEach((requirement) => {
        if (requirement.name === this.item.template.name) {
          mdmRequirement = requirement.requirement;
        }
      });
      return mdmRequirement;
    },

    redirectTo() {
      if (this.isOsPatchManagementPolicy) {
        return `${ItemTypeRoutes[ItemTypeConsts.osPatchManagement]}`;
      }
      if (this.isBrowserPatchManagementPolicy) {
        return `${ItemTypeRoutes[ItemTypeConsts.browserPatchManagement]}`;
      }
      return ItemTypeRoutes[ItemTypeConsts.configuration];
    },

    showRecoveryKeyButton() {
      let showRecoveryKeyButton = false;
      if (this.item.template) {
        const { template } = this.item;
        const { FILE_VAULT_TEMPLATE_NAME, BITLOCKER_TEMPLATE_NAME } = PolicyConsts;
        if (template.name === FILE_VAULT_TEMPLATE_NAME
          || template.name === BITLOCKER_TEMPLATE_NAME) {
          showRecoveryKeyButton = true;
        }
      }
      return showRecoveryKeyButton;
    },
  },

  beforeMount() {
    this.setItemField({ key: 'templateId', value: this.templateId });
  },

  methods: {
    ...mapActions(configurationModel, [
      'bindConfigurationToConfigurationGroups',
      'bindConfigurationToSystems',
      'bindConfigurationToSystemGroups',
      'fetchItem',
      'saveItem',
      'setItemField',
    ]),

    ...mapActions(StoreModuleMap[policy], {
      policyRefreshCollection: 'refreshCollection',
    }),

    ...mapActions(StoreModuleMap[browserPatchManagement], {
      BpmPolicyRefreshCollection: 'refreshCollection',
    }),

    ...mapActions(StoreModuleMap[osPatchManagement], {
      OsPolicyRefreshCollection: 'refreshCollection',
    }),

    ...mapActions('PolicySystemStatusCollection', [
      'fetchFilteredTotalCount',
      'setPolicyId',
    ]),

    ...mapGetters(systemCollection, {
      getSystemsToBind: 'getItemsToBind',
      getSystemsToUnbind: 'getItemsToUnbind',
    }),

    ...mapGetters(policyGroupCollection, {
      getPolicyGroupsToBind: 'getItemsToBind',
      getPolicyGroupsToUnbind: 'getItemsToUnbind',
    }),

    ...mapGetters(systemGroupCollection, {
      getSystemGroupsToBind: 'getItemsToBind',
      getSystemGroupsToUnbind: 'getItemsToUnbind',
    }),

    confirmSave(devicesTotalCount) {
      // eslint-disable-next-line consistent-return
      return new Promise((resolve) => {
        const selectedSystemCount = this.getSystemsToBind().length || devicesTotalCount.bind;
        const deselectedSystemCount = this.getSystemsToUnbind().length || devicesTotalCount.unbind;
        const selectedSystemGroupCount = this.getSystemGroupsToBind().length;
        const deselectedSystemGroupCount = this.getSystemGroupsToUnbind().length;
        const selectedPolicyGroupCount = this.getPolicyGroupsToBind().length;
        const deselectedPolicyGroupCount = this.getPolicyGroupsToUnbind().length;

        // if we are not adjusting the bindings, just save the configuration information
        if (
          !selectedSystemCount
          && !deselectedSystemCount
          && !selectedSystemGroupCount
          && !deselectedSystemGroupCount
          && !selectedPolicyGroupCount
          && !deselectedPolicyGroupCount
        ) {
          return resolve({ canContinue: true, hasBoundDevices: false });
        }

        ModalEventBus.open({
          component: ConfigurationActivationModal,
          componentProps: {
            configuration: this.item,
            handleCancel: () => {
              resolve({ canContinue: false, hasBoundDevices: false });
              ModalEventBus.close();
            },
            handleContinue: () => {
              resolve({
                canContinue: true,
                hasBoundDevices: selectedSystemCount || selectedSystemGroupCount,
              });
              ModalEventBus.close();
            },
            selectedSystemCount,
            deselectedSystemCount,
            selectedSystemGroupCount,
            deselectedSystemGroupCount,
            selectedPolicyGroupCount,
            deselectedPolicyGroupCount,
          },
        });
      });
    },

    handleBackClick() {
      if (this.isOsPatchManagementPolicy) {
        return Util.redirect(ItemTypeRoutes[ItemTypeConsts.osPatchManagement]);
      }

      if (this.isBrowserPatchManagementPolicy) {
        return Util.redirect(ItemTypeRoutes[ItemTypeConsts.browserPatchManagement]);
      }

      if (this.isItemBeingUpdated) {
        return Util.redirect(ItemTypeRoutes[this.itemType]);
      }

      const os = this.item.template.osMetaFamily || '';

      return Util.redirect(`${ItemTypeRoutes[this.itemType]}/choose/${os.toLowerCase()}`);
    },

    async handleMounted() {
      if (this.navigateToTab) {
        this.navigateToAsideTab(this.navigateToTab.tabLabel);
      }
      await this.renderPolicyStatusTab();
    },

    async handleSave() {
      const totalDevicesCount = {
        bind: 0,
        unbind: 0,
      };

      await this.$validator.validate();

      if (this.$validator.errors.any()) {
        throw Error(this.validationErrorMessage);
      }

      // Get devices count for universal policies
      if (this.item.template && this.item.template.osMetaFamily === universal) {
        totalDevicesCount.bind = sum(
          await Promise.all(
            this.getSystemGroupsToBind().map(async (deviceGroup) => {
              const devicesCount = await SystemGroupApi.getMembership(deviceGroup.id);
              return parseInt(devicesCount.headers['x-total-count'], 10);
            }),
          ),
        );

        totalDevicesCount.unbind = sum(
          await Promise.all(
            this.getSystemGroupsToUnbind().map(async (deviceGroup) => {
              const devicesCount = await SystemGroupApi.getMembership(deviceGroup.id);
              return parseInt(devicesCount.headers['x-total-count'], 10);
            }),
          ),
        );
      }

      const { canContinue } = await this.confirmSave(totalDevicesCount);

      if (canContinue === false) {
        return new SaveReturnValue({ canContinue });
      }

      this.itemHasBeenSaved = true;

      const item = await this.saveItem();

      await Promise.all([
        this.bindConfigurationToSystems(),
        this.bindConfigurationToSystemGroups(),
        this.bindConfigurationToConfigurationGroups(),
      ]);

      if (this.isBrowserPatchManagementPolicy) {
        await this.BpmPolicyRefreshCollection({ item });
      } else if (this.isOsPatchManagementPolicy) {
        await this.OsPolicyRefreshCollection({ item });
      } else {
        await this.policyRefreshCollection({ item });
      }
      this.refreshTrialData('osPatchManagement');
      return new SaveReturnValue();
    },

    navigateToAsideTab(tabLabel) {
      this.activeTab = { tabLabel };
    },

    async renderPolicyStatusTab() {
      if (this.isItemBeingUpdated) {
        const policyItem = this.item;
        await this.setPolicyId(policyItem.id);
        this.statusCount = await this.fetchFilteredTotalCount();
      }
    },

    showAddOnTrialModal() {
      ModalEventBus.open({
        component: 'TrialModal',
        componentProps: trialPropsData.osPatchManagement,
        isLargeModal: true,
      });
    },
  },
};
</script>
<style module>
.macAlert {
  background-color: var(--jcTertiary50);
  border: var(--jc-border-width) solid var(--jcTertiary50);
  font-size: 1rem;
  padding: 10px;
}

.requiresSupervision {
  margin-bottom: 1.5rem;
}
</style>
